import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"

type Variant = "light" | "primary" | "dark"

interface StyledProps {
  checked: boolean
  disabled: boolean
}

const Dark = styled.svg<StyledProps>`
  rect,
  path {
    stroke: ${(props) => props.theme.palette.content.neutral.default};
  }
  min-width: 14px;
  width: 14px;
  min-height: 14px;
  height: 14px;

  ${(props) =>
    !props.checked &&
    css`
      path {
        stroke: transparent;
      }
    `}
`

const Primary = styled.svg<StyledProps>`
  min-width: 20px;
  width: 20px;
  min-height: 20px;
  height: 20px;
  rect {
    fill: ${(props) => props.theme.palette.content.secondary.default};
  }
  path {
    stroke: ${(props) => props.theme.palette.content.neutral.inverse};
  }

  ${(props) =>
    !props.checked &&
    css`
      border-radius: 2px;
      rect {
        fill: ${props.theme.palette.content.neutral.inverse};
        stroke: ${props.theme.palette.border.neutral.default};
        stroke-width: 2;
      }
    `}

  ${(props) =>
    props.disabled &&
    css`
      rect {
        ${props.checked
          ? css`
              fill: ${props.theme.palette.surface.secondary.disabled};
              stroke: ${props.theme.palette.surface.secondary.disabled};
            `
          : css`
              fill: ${props.theme.palette.surface.neutral.disabled};
              stroke: ${props.theme.palette.border.neutral.default};
            `}
      }
      path {
        stroke: ${props.checked
          ? props.theme.palette.content.neutral.inverse
          : "transparent"};
      }
    `}
`

const PrimaryLight = styled.svg<StyledProps>`
  min-width: 22px;
  width: 22px;
  min-height: 22px;
  height: 22px;
  rect {
    stroke-width: 2;
    fill: ${(props) => props.theme.palette.surface.secondary.strong};
    stroke: ${(props) => props.theme.palette.surface.secondary.strong};
  }
  path {
    stroke: ${(props) => props.theme.palette.content.neutral.inverse};
    stroke-width: 2;
  }

  ${(props) =>
    !props.checked &&
    css`
      border-radius: 2px;
      rect {
        fill: ${props.theme.palette.content.neutral.inverse};
        stroke: ${props.theme.palette.border.neutral.default};
        stroke-width: 1;
      }
    `}

  ${(props) =>
    props.disabled &&
    css`
      rect {
        stroke-width: 2;
        ${props.checked
          ? css`
              fill: ${props.theme.palette.surface.secondary.disabled};
              stroke: ${props.theme.palette.surface.secondary.disabled};
            `
          : css`
              fill: ${props.theme.palette.surface.neutral.disabled};
              stroke: ${props.theme.palette.border.neutral.default};
            `}
      }
      path {
        stroke: ${props.checked
          ? props.theme.palette.content.neutral.inverse
          : "transparent"};
      }
    `}
`

const Checkbox = (props: {
  className?: string
  checked: boolean
  disabled: boolean
  variant: Variant
}) => {
  switch (props.variant) {
    case "primary":
      return (
        <Primary
          checked={props.checked}
          disabled={props.disabled}
          focusable="false"
          className={props.className}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="20" height="20" rx="2" />
          <path d="M4 10L8 14L16 6" strokeWidth="2" />
        </Primary>
      )
    case "light":
      return (
        <PrimaryLight
          checked={props.checked}
          disabled={props.disabled}
          focusable="false"
          className={props.className}
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="1" y="1" width="20" height="20" rx="1" />
          <path d="M6 11.5L9.5 15L16.5 8" />
        </PrimaryLight>
      )
    case "dark":
    default:
      return (
        <Dark
          checked={props.checked}
          disabled={props.disabled}
          focusable="false"
          className={props.className}
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" />
          <path d="M3 7L5.5 9.5L11 4" />
        </Dark>
      )
  }
}

export default Checkbox
